import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetSpecialOffersQuery,
  useDeleteSpecialOfferMutation,
  useUpdateSpecialOfferMutation,
} from "../redux/services/api";
import { selectIsAuthenticated, selectUser } from "../redux/slices/authSlice";
import Icon from "../components/Icon";
import { createExcerpt } from "../utils/excerpt";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";
import { useLanguage } from "../contexts/LanguageContext";

const SpecialOffers = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  // RTK Query hooks
  const { data: offersData, isLoading: loading } = useGetSpecialOffersQuery();
  const [deleteOffer] = useDeleteSpecialOfferMutation();
  const [updateOffer] = useUpdateSpecialOfferMutation();

  const offers = offersData?.offers || [];
  const [deleteStatus, setDeleteStatus] = useState("");
  const messageType = useMessageType(deleteStatus);
  
  // Optimistic state for offer status updates
  const [optimisticStatus, setOptimisticStatus] = useState({});
  
  // Clear optimistic status when actual data matches optimistic state
  useEffect(() => {
    if (offers.length > 0) {
      setOptimisticStatus((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((offerId) => {
          const offer = offers.find((o) => o.id === parseInt(offerId));
          if (offer && offer.is_active === updated[offerId]) {
            delete updated[offerId];
          }
        });
        return updated;
      });
    }
  }, [offers]);

  const handleDelete = async (offerId) => {
    if (!window.confirm(t("specialOffersDashboard.deleteConfirmMessage"))) {
      return;
    }

    try {
      await deleteOffer(offerId).unwrap();
      setDeleteStatus(t("specialOffersDashboard.offerDeletedSuccessfully"));
      setTimeout(() => setDeleteStatus(""), 3000);
    } catch (error) {
      console.error("Error deleting offer:", error);
      setDeleteStatus(
        error.data?.message || t("specialOffersDashboard.errorDeletingOffer")
      );
    }
  };

  const handleEdit = (offerId) => {
    navigate(getLocalizedPath(`/dashboard/special-offers/edit/${offerId}`));
  };

  const handleAddNew = () => {
    navigate(getLocalizedPath("/dashboard/special-offers/add"));
  };

  const handleToggleStatus = async (offer, e) => {
    e.stopPropagation();
    
    // Optimistic update - immediately change the status
    const newStatus = !offer.is_active;
    setOptimisticStatus((prev) => ({
      ...prev,
      [offer.id]: newStatus,
    }));
    
    try {
      const formData = new FormData();
      formData.append("title", offer.title || "");
      formData.append("content", offer.content || "");
      formData.append("is_active", newStatus ? "1" : "0");
      if (offer.title_ar) {
        formData.append("titleAr", offer.title_ar);
      }
      if (offer.content_ar) {
        formData.append("contentAr", offer.content_ar);
      }
      if (offer.keywords) {
        formData.append("keywords", offer.keywords || "");
      }

      await updateOffer({
        id: offer.id,
        body: formData,
      }).unwrap();
      
      // Don't clear optimistic status here - let useEffect handle it when data refetches
      setDeleteStatus(
        offer.is_active
          ? t("specialOffersDashboard.offerDeactivatedSuccessfully") ||
              "Offer deactivated successfully!"
          : t("specialOffersDashboard.offerActivatedSuccessfully") ||
              "Offer activated successfully!"
      );
      setTimeout(() => setDeleteStatus(""), 3000);
    } catch (error) {
      console.error("Error updating offer status:", error);
      
      // Revert optimistic update on error
      setOptimisticStatus((prev) => {
        const updated = { ...prev };
        delete updated[offer.id];
        return updated;
      });
      
      setDeleteStatus(
        error.data?.message ||
          t("specialOffersDashboard.errorUpdatingOffer") ||
          "Error updating offer status"
      );
    }
  };
  
  // Get the effective status (optimistic or actual)
  const getOfferStatus = (offer) => {
    if (offer.id in optimisticStatus) {
      return optimisticStatus[offer.id];
    }
    return offer.is_active;
  };

  const formatContent = (content) => {
    // Use excerpt utility to create 15-word excerpt
    return createExcerpt(content, 15);
  };

  if (loading) {
    return (
      <div className="special-offers">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("specialOffersDashboard.loadingOffers")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="special-offers">
        <div className="auth-required">
          <h2>{t("specialOffersDashboard.authenticationRequired")}</h2>
          <p>{t("specialOffersDashboard.pleaseLoginToManage")}</p>
          <button
            className="login-btn"
            onClick={() => navigate(getLocalizedPath("/login"))}
          >
            {t("specialOffersDashboard.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="special-offers">
      <div className="page-header">
        <div className="header-content">
          <h1>{t("offers.title")}</h1>
          <p>{t("specialOffersDashboard.manageYourOffers")}</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="add-offer-btn"
            onClick={handleAddNew}
          >
            <Icon name="plus" size="16px" />
            {t("common.addNew")} {t("offers.title")}
          </button>
        </div>
      </div>

      <StatusMessage
        message={deleteStatus}
        type={messageType}
        show={!!deleteStatus}
        autoClose={messageType === "success"}
        autoCloseDelay={5000}
        onClose={() => setDeleteStatus("")}
      />

      <div className="offers-table-container">
        {offers.length === 0 ? (
          <div className="empty-offers">
            <div className="empty-content">
              <Icon name="tag" size="48px" />
              <h3>{t("offers.noOffers")}</h3>
              <p>{t("specialOffersDashboard.addFirstOfferDescription")}</p>
              <button
                type="button"
                className="add-first-offer-btn"
                onClick={handleAddNew}
              >
                <Icon name="plus" size="16px" />
                {t("specialOffersDashboard.addFirstOffer")}
              </button>
            </div>
          </div>
        ) : (
          <div className="offers-table">
            <table>
              <thead>
                <tr>
                  <th>{t("specialOffersDashboard.image")}</th>
                  <th>{t("specialOffersDashboard.title")}</th>
                  <th>{t("specialOffersDashboard.contentPreview")}</th>
                  <th>{t("specialOffersDashboard.status")}</th>
                  <th>{t("specialOffersDashboard.created")}</th>
                  <th>{t("specialOffersDashboard.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((offer) => (
                  <tr key={offer.id}>
                    <td className="image-cell">
                      {offer.image_url ? (
                        <img
                          src={offer.image_url}
                          alt={offer.title}
                          className="offer-thumbnail"
                        />
                      ) : (
                        <div className="no-image">
                          <Icon name="image" size="24px" />
                        </div>
                      )}
                    </td>
                    <td className="title-cell">
                      <strong>{offer.title}</strong>
                    </td>
                    <td className="content-cell">
                      {formatContent(offer.content)}
                    </td>
                    <td className="status-cell">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={getOfferStatus(offer)}
                          onChange={(e) => handleToggleStatus(offer, e)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </td>
                    <td className="date-cell">
                      {new Date(offer.created_at).toLocaleDateString()}
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => handleEdit(offer.id)}
                          title={t("specialOffersDashboard.editOffer")}
                        >
                          <Icon name="edit" size="14px" />
                        </button>
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={() => handleDelete(offer.id)}
                          title={t("specialOffersDashboard.deleteOffer")}
                        >
                          <Icon name="trash" size="14px" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecialOffers;
